/**
 * ItemSelect - Selector de ítem individual con búsqueda
 * 
 * Versión simplificada de ItemSearchSelector para campos que esperan
 * un único valor numérico (como item_id en forms).
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Select, Spin, Empty } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { debounce } from 'lodash';

import {
    usePosItemsSearch,
    type PosItemType,
    type PosServiceKind,
} from '@features/erp/pos/api/usePosItemsSearch.ts';
import {
    trackItemSelectedForContext,
    trackSearchEmptyResult,
    trackSearchPerformed,
    trackSearchResultSelected,
    type BehaviorAnalyticsContext,
} from '@tools/behaviorAnalytics.ts';

type ItemSelectOption = DefaultOptionType & {
    itemType?: string | null;
};

export interface ItemSelectProps {
    value?: number;
    onChange?: (value: number | undefined) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    allowClear?: boolean;
    disabled?: boolean;
    itemType?: PosItemType;
    allowedItemTypes?: PosItemType[];
    serviceKind?: PosServiceKind;
    categoryIds?: number[];
    initialItem?: { id: number; name: string; sku?: string | null };
    behaviorContext?: BehaviorAnalyticsContext;
}

export const ItemSelect: React.FC<ItemSelectProps> = ({
    value,
    onChange,
    placeholder = 'Buscar ítem...',
    style,
    allowClear = true,
    disabled = false,
    itemType,
    allowedItemTypes,
    serviceKind,
    categoryIds,
    initialItem,
    behaviorContext,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);
    const lastTrackedSearchRef = useRef<string | null>(null);

    const { data: searchResults, isLoading, isFetching } = usePosItemsSearch(
        {
            q: searchTerm,
            itemType,
            allowedItemTypes,
            serviceKind,
            categoryIds,
            page: 1,
            pageSize: 50,
        },
        {
            enabled: open || searchTerm.length > 0,
            keepPreviousData: true,
        },
    );

    const handleSearch = useCallback(
        debounce((val: string) => {
            setSearchTerm(val);
        }, 300),
        [],
    );

    const searchId = useMemo(
        () => [
            'item_select',
            behaviorContext?.componentId ?? 'item_search',
            searchTerm.trim() || 'empty',
            itemType ?? 'all',
            categoryIds?.join('-') || 'all_categories',
        ].join(':'),
        [behaviorContext?.componentId, categoryIds, itemType, searchTerm],
    );

    const handleChange = useCallback(
        (newValue: number | undefined, option?: DefaultOptionType | DefaultOptionType[]) => {
            if (newValue !== undefined && behaviorContext) {
                const selectedOption = Array.isArray(option) ? option[0] : option as ItemSelectOption | undefined;
                trackSearchResultSelected(behaviorContext, {
                    search_id: searchId,
                    result_type: 'item',
                    result_id: String(newValue),
                    query_length: searchTerm.trim().length,
                    source: searchTerm.trim() ? 'manual_search' : 'selector',
                });
                trackItemSelectedForContext(behaviorContext, {
                    item_id: String(newValue),
                    item_type: selectedOption?.itemType ?? itemType ?? null,
                    search_id: searchId,
                    source: searchTerm.trim() ? 'manual_search' : 'selector',
                });
            }
            onChange?.(newValue);
        },
        [behaviorContext, itemType, onChange, searchId, searchTerm],
    );

    const handleDropdownVisibleChange = useCallback(
        (visible: boolean) => {
            setOpen(visible);
            if (!visible) {
                setSearchTerm('');
            }
        },
        [],
    );

    const options = useMemo<ItemSelectOption[]>(() => {
        const results = (searchResults?.results || []).filter(
            (item) => item.item_type !== 'subscription_plan',
        );

        const mapped: ItemSelectOption[] = results.map((item) => ({
            label: `${item.name}${item.sku ? ` (${item.sku})` : ''}`,
            value: item.id,
            itemType: item.item_type,
        }));

        if (initialItem && value === initialItem.id) {
            const exists = mapped.find((o) => o.value === initialItem.id);
            if (!exists) {
                mapped.unshift({
                    label: `${initialItem.name}${initialItem.sku ? ` (${initialItem.sku})` : ''}`,
                    value: initialItem.id,
                    itemType,
                });
            }
        }

        return mapped;
    }, [initialItem, itemType, searchResults, value]);

    useEffect(() => {
        if (!behaviorContext || !open || isFetching) {
            return;
        }
        const trimmed = searchTerm.trim();
        const resultCount = options.length;
        const status = trimmed && resultCount === 0 ? 'empty' : 'ok';
        const trackingKey = `${searchId}:${status}:${resultCount}`;
        if (lastTrackedSearchRef.current === trackingKey) {
            return;
        }
        lastTrackedSearchRef.current = trackingKey;
        const payload = {
            search_id: searchId,
            query_length: trimmed.length,
            result_count: resultCount,
            filters_count: Number(Boolean(itemType)) + (categoryIds?.length ?? 0),
            has_query: trimmed.length > 0,
            status,
        };
        trackSearchPerformed(behaviorContext, payload);
        if (trimmed && resultCount === 0) {
            trackSearchEmptyResult(behaviorContext, payload);
        }
    }, [behaviorContext, categoryIds?.length, isFetching, itemType, open, options.length, searchId, searchTerm]);

    const notFoundContent = useMemo(() => {
        if (isLoading || isFetching) {
            return <Spin size="small" />;
        }
        if (searchTerm && options.length === 0) {
            return <Empty description="No se encontraron ítems" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }
        return <Empty description="Escribí para buscar..." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }, [isLoading, isFetching, searchTerm, options.length]);

    return (
        <Select
            value={value}
            onChange={handleChange}
            onSearch={handleSearch}
            onOpenChange={handleDropdownVisibleChange}
            placeholder={placeholder}
            style={style}
            allowClear={allowClear}
            disabled={disabled}
            showSearch
            filterOption={false}
            options={options}
            notFoundContent={notFoundContent}
            loading={isLoading || isFetching}
            aria-label="Buscar y seleccionar ítem"
        />
    );
};

ItemSelect.displayName = 'ItemSelect';

export default ItemSelect;
